import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CallCostsDialogComponent } from 'src/app/common-components/call-costs-dialog/call-costs-dialog.component';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ProviderCallCostSaveModel } from '../models/add-provider-call-costs-data';
import { AddProviderCallCostApiService } from '../services/add-provider-call-costs-api.service';
import * as AddProviderCallCostActions from './add-provider-call-costs.actions';

@Injectable()
export class AddProviderCallCostEffects {
   createProviderCallCostPreview$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddProviderCallCostActions.createProviderCallCostPreview),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action]) => {
            const providerCallCostToSave: ProviderCallCostSaveModel = {
               ...action.providerCallCost,
            };
            return this.addProviderCallCostApiService.uploadPreview(providerCallCostToSave).pipe(
               map((executionResult) => {
                  return AddProviderCallCostActions.createProviderCallCost({
                     providerCallCostPreview: executionResult,
                     providerCallCost: providerCallCostToSave,
                  });
               }),
               catchError((error) => {
                  return of(AddProviderCallCostActions.createProviderCallCostFailure(error));
               })
            );
         })
      );
   });

   createProviderCallCost$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddProviderCallCostActions.createProviderCallCost),
            map((executionResult) => {
               this.dialogService.closeFormComponent();
               const dialogRef = this.dialog.open(CallCostsDialogComponent, {
                  data: { callCosts: executionResult },
                  width: '800px',
                  disableClose: true,
               });
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createProviderCallCostsUpload$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddProviderCallCostActions.createProviderCallCostUpload),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action]) => {
            const providerCallCostToSave: ProviderCallCostSaveModel = {
               ...action.providerCallCost,
            };
            return this.addProviderCallCostApiService.upload(providerCallCostToSave).pipe(
               map((executionResult) => {
                  return AddProviderCallCostActions.createProviderCallCostSuccess({
                     providerCallCostId: 0,
                  });
               }),
               catchError((error) => {
                  return of(AddProviderCallCostActions.createProviderCallCostFailure(error));
               })
            );
         })
      );
   });

   createProviderCallCostSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddProviderCallCostActions.createProviderCallCostSuccess),
            tap(() => {
               this.dialogService.closeFormComponent();
               this.snackbar.open('Call Costs added successfully');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createProviderCallCostFailure$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddProviderCallCostActions.createProviderCallCostFailure),
            tap(() => {
               this.snackbar.open('Unable to parse file');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   constructor(
      private actions$: Actions,
      private addProviderCallCostApiService: AddProviderCallCostApiService,
      private store: Store,
      private snackbar: MatSnackBar,
      private dialogService: LcsDialogService,
      private dialog: MatDialog
   ) {}
}
